/**
 * Keep a draft on screen in step with the server while another device edits it (tradeAutoRefresh.ts):
 * poll its version while the tab is visible, re-read the trade when it moved on, and hand it over —
 * but never while this page has an edit of its own in flight (`busy`). A change seen while busy is
 * fetched as soon as the page is idle again, and an answer that arrives after an edit started is
 * dropped and asked for again later, so a re-read can never overwrite what staff are doing.
 */
import { useCallback, useEffect, useRef } from 'react'
import { api } from '../../../utils/api'
import { refreshAction, versionMoved, TRADE_POLL_MS, type TradeVersion } from './tradeAutoRefresh'

type Known = { updatedAt?: string | null; status?: string } | null

export function useTradeAutoRefresh<T extends { id: string; status?: string }>({
  tradeId,
  known,
  busy,
  onTrade,
  onMissing,
  intervalMs = TRADE_POLL_MS,
}: {
  /** The draft on screen; null turns polling off. Polling also stops once it isn't a draft. */
  tradeId: string | null
  /** The copy on screen (its updatedAt and status). */
  known: Known
  /** An edit of this page's own is in flight, or an editor is open: don't replace the trade now. */
  busy: boolean
  /** The trade as the server has it now (only for `tradeId`, only while not busy). */
  onTrade: (trade: T) => void
  /** The trade is gone (deleted, or not this store's). */
  onMissing?: () => void
  intervalMs?: number
}): { refreshNow: () => void } {
  const knownRef = useRef(known)
  knownRef.current = known
  const busyRef = useRef(busy)
  busyRef.current = busy
  const onTradeRef = useRef(onTrade)
  onTradeRef.current = onTrade
  const onMissingRef = useRef(onMissing)
  onMissingRef.current = onMissing
  const tradeIdRef = useRef(tradeId)
  tradeIdRef.current = tradeId
  /** A change was seen while busy: fetch it as soon as the page is idle. */
  const pendingRef = useRef(false)
  const fetchingRef = useRef(false)

  const refresh = useCallback(async (id: string) => {
    if (fetchingRef.current) {
      pendingRef.current = true
      return
    }
    fetchingRef.current = true
    try {
      const res = await api.get(`/trades/${id}`)
      const trade = res.data?.data as T | undefined
      if (!trade || tradeIdRef.current !== id) return
      if (busyRef.current) { // an edit started while this was on its way: its answer is newer
        pendingRef.current = true
        return
      }
      pendingRef.current = false
      onTradeRef.current(trade)
    } catch (err: unknown) {
      if ((err as { response?: { status?: number } })?.response?.status === 404 && tradeIdRef.current === id) onMissingRef.current?.()
    } finally {
      fetchingRef.current = false
    }
  }, [])

  const draft = known?.status === undefined || known.status === 'draft'
  useEffect(() => {
    if (!tradeId || !draft) return
    let stopped = false
    let polling = false
    const tick = async () => {
      if (polling || stopped || document.visibilityState !== 'visible') return
      polling = true
      try {
        const res = await api.get(`/trades/${tradeId}/version`)
        const version = res.data?.data as TradeVersion | undefined
        if (stopped || !version) return
        const action = refreshAction(versionMoved(knownRef.current, version), busyRef.current)
        if (action === 'refresh') await refresh(tradeId)
        else if (action === 'defer') pendingRef.current = true
      } catch (err: unknown) {
        if (!stopped && (err as { response?: { status?: number } })?.response?.status === 404) onMissingRef.current?.()
      } finally {
        polling = false
      }
    }
    const timer = setInterval(() => { void tick() }, intervalMs)
    const onVisible = () => { if (document.visibilityState === 'visible') void tick() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stopped = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [tradeId, draft, intervalMs, refresh])

  // A change seen while busy: fetch it the moment the page is idle again.
  useEffect(() => {
    if (busy || !pendingRef.current || !tradeId) return
    void refresh(tradeId)
  }, [busy, tradeId, refresh])

  // A different trade on screen: nothing seen for the old one applies.
  useEffect(() => { pendingRef.current = false }, [tradeId])

  const refreshNow = useCallback(() => { if (tradeIdRef.current) void refresh(tradeIdRef.current) }, [refresh])
  return { refreshNow }
}
